import type { StaticMissionStoryContent, StaticPageKey } from './staticPageContent.js';
import { action, card, section, type LocaleWithoutGerman, type LocalizedPageCopy } from './staticPageLocalizationModel.js';
import {
  enGlobusJobs,
  enGlobusJobsOverview,
  trGlobusJobs,
  trGlobusJobsOverview,
} from './globusJobLocalization.js';

const missionStory: Record<LocaleWithoutGerman, StaticMissionStoryContent> = {
  tr: {
    eyebrow: 'Hikâyemiz',
    title: 'Sınırları aşmak, insanları buluşturmak.',
    slogan: 'İnsan kaynakları hizmeti insanla başlar.',
    intro:
      'Viva Porta 2025 yılında net bir inançla kuruldu. Alman-Türk kökleri, girişimcilik deneyimi ve sosyal sorumluluk anlayışı; insanları yalnızca birer profil olarak görmeyen bir işe yerleştirme yaklaşımında birleşti.',
    blocks: [
      {
        title: 'Başlangıç',
        paragraphs: [
          {
            text: 'Viva Porta 2025 yılında net bir inançla kuruldu: Başarılı insan kaynakları hizmeti insanla başlar.',
            parts: [],
          },
          {
            text: 'Almanya ve Türkiye’deki köklerimiz sayesinde iki kültürü de kendi deneyimimizden tanıyor, insanlar ile şirketler arasında sınırları aşan bağlar kuruyoruz. Böylece iki ülke arasında bir köprü oluşturuyor ve sürdürülebilir iş birliği için yeni perspektifler açıyoruz.',
            parts: [],
          },
          {
            text: 'Ekonomik ve toplumsal alanlarda yıllardır bize güvenen bir ağın üzerine açık bir misyon kurduk: uzman açığına sürdürülebilir çözümler üretmek, Almanya’daki şirketleri motive ve nitelikli çalışanlarla desteklemek ve burada mesleki bir gelecek kurmak isteyen insanlara yeni fırsatlar sunmak.',
            parts: [],
          },
        ],
      },
      {
        title: 'Arkasındaki insanlar',
        paragraphs: [
          {
            text: 'Kurucular Wilhelm Josten ve Enes Vural girişimcilik deneyimini, yenilik gücünü ve her şirketin onu taşıyan insanlar kadar güçlü olduğu inancını bir araya getiriyor.',
            parts: [],
          },
          {
            text: 'Onları, sosyal girişimcilik alanındaki uzun yıllara dayanan deneyimini Viva Porta’nın gelişimine aktaran Murat Vural destekliyor.',
            parts: [],
          },
        ],
      },
      {
        title: 'Girişimcilik ve sosyal deneyim',
        paragraphs: [
          {
            text: 'Wilhelm Josten köklü bir girişimci aileden geliyor ve başarılı BUTLERS mağaza zinciriyle bugün Almanya çapında tanınan bir marka yarattı. Pazar sezgisi, yenilikçi yaklaşımı ve sürdürülebilir büyüme anlayışı Viva Porta’nın stratejik yönünü şekillendiriyor.',
            parts: [],
          },
          {
            text: 'Elektrik mühendisi Murat Vural yirmi yılı aşkın süredir adil eğitim fırsatları için çalışıyor. Federal Liyakat Nişanı sahibi ve kâr amacı gütmeyen bir sosyal girişimin kurucusu ve yöneticisi olarak insanlara perspektif kazandırmaya ve potansiyellerini geliştirmeye odaklanıyor.',
            parts: [],
          },
          {
            text: 'Murat Vural için insan her zaman çalışmaların merkezindedir. Sürdürülebilir şirket başarısının ancak güçlü bir ekiple mümkün olduğuna inanır ve kararlarını daima anlam ve amaç odaklı alır.',
            parts: [],
          },
        ],
      },
      {
        title: 'Operasyonel yakınlık',
        paragraphs: [
          {
            text: 'Genel Müdür Enes Vural, Viva Porta’nın operasyonel ve stratejik gelişiminden sorumludur. Satış deneyimi, sosyal alandaki uzun yıllara dayanan çalışmaları ve insanlara dair güçlü anlayışı yaklaşımının temelini oluşturur. Onun için her başvurunun arkasında kişisel bir hikâye, her açık pozisyonun arkasında hedefler, sorumluluk ve gelecek planları vardır.',
            parts: [],
          },
          {
            text: 'Teyzesi Serife Vural-Banik de uzun yıllara dayanan liderlik ve yönetim deneyimiyle Viva Porta’yı destekliyor. Bir sosyal girişimin yönetim ekibinin bir üyesi olarak güçlü organizasyon, strateji ve personel yönetimi becerileri getiriyor. Tıbbi asistanlık eğitimi sayesinde sağlık ve bakım alanına ilişkin değerli deneyime de sahip.',
            parts: [],
          },
        ],
      },
      {
        title: 'Temelimiz',
        paragraphs: [
          {
            text: 'Wilhelm Josten ve Murat Vural ekonomik ve sosyal alanlarda toplamda neredeyse yarım yüzyıllık girişimcilik deneyimini bir araya getiriyor. Bu bilgi Viva Porta’nın temelini oluşturuyor.',
            parts: [],
          },
          {
            text: 'Bizi asıl farklı kılan inancımızdır: İnsan kaynakları hizmeti yalnızca çalışan yerleştirmek değildir. Güven, sorumluluk ve insanlara yollarında eşlik etmek demektir.',
            parts: [],
          },
        ],
      },
    ],
    closing: {
      title: 'Çünkü sonunda farkı her zaman insanlar yaratır.',
      paragraphs: [
        {
          text: 'Her mesleki kararın arkasında umutlar, hayaller, aileler ve gelecek planları olduğunu biliyoruz. Bu nedenle herkese saygı, dikkat ve değer vererek yaklaşıyoruz.',
          parts: [],
        },
        {
          text: 'Amacımız şirketleri ve insanları yalnızca bir sonraki görev için değil, her iki tarafın uzun vadeli başarısı için sürdürülebilir biçimde bir araya getirmektir.',
          parts: [],
        },
        { text: 'Viva Porta ortaklığa dayalı iş birliği, güvenilirlik ve gerçek insanlık için çalışır.', parts: [] },
      ],
    },
  },
  en: {
    eyebrow: 'Our story',
    title: 'Crossing borders, connecting people.',
    slogan: 'Staffing begins with people.',
    intro:
      'Viva Porta was founded in 2025 with a clear conviction. German-Turkish roots, entrepreneurial experience and social responsibility came together in a recruitment approach that never reduces people to profiles.',
    blocks: [
      {
        title: 'The beginning',
        paragraphs: [
          {
            text: 'Viva Porta was founded in 2025 with a clear conviction: successful staffing begins with people.',
            parts: [],
          },
          {
            text: 'Our roots in Germany and Türkiye allow us to understand both cultures from personal experience and connect people and companies across borders. We build a bridge between two countries and open new perspectives for sustainable collaboration.',
            parts: [],
          },
          {
            text: 'Building on a network that has trusted us for many years in business and society, we pursue a clear mission: address the skills shortage sustainably, support German companies with committed and qualified employees, and create new opportunities for people who want to build a professional future here.',
            parts: [],
          },
        ],
      },
      {
        title: 'The people behind it',
        paragraphs: [
          {
            text: 'Founders Wilhelm Josten and Enes Vural combine entrepreneurial experience, innovative drive and the firm belief that every company is only as strong as the people who carry it.',
            parts: [],
          },
          {
            text: 'They are supported by Murat Vural, who contributes his many years of experience as a social entrepreneur to the development of Viva Porta.',
            parts: [],
          },
        ],
      },
      {
        title: 'Entrepreneurial and social experience',
        paragraphs: [
          {
            text: 'Wilhelm Josten comes from a long-established entrepreneurial family and created BUTLERS, a successful retail brand known throughout Germany. His market instinct, innovative drive and understanding of sustainable growth shape Viva Porta’s strategic direction.',
            parts: [],
          },
          {
            text: 'Electrical engineer Murat Vural has worked for fair educational opportunities for more than two decades. A recipient of Germany’s Federal Cross of Merit and founder and director of a non-profit social enterprise, he is committed to opening opportunities and developing potential.',
            parts: [],
          },
          {
            text: 'For Murat Vural, people are always at the centre. He believes sustainable business success can only grow from a strong team and has always made his decisions with purpose.',
            parts: [],
          },
        ],
      },
      {
        title: 'Close to daily operations',
        paragraphs: [
          {
            text: 'As Managing Director, Enes Vural is responsible for Viva Porta’s operational and strategic development. His sales experience, long-standing work in the non-profit sector and strong understanding of people form the basis of his approach. Every application carries a personal story; every vacancy carries goals, responsibility and future prospects.',
            parts: [],
          },
          {
            text: 'His aunt Serife Vural-Banik also supports Viva Porta with many years of leadership experience. As a member of a social enterprise’s management team, she contributes strong organisational, strategic and people-management skills. Her training as a medical assistant also provides valuable insight into healthcare and nursing.',
            parts: [],
          },
        ],
      },
      {
        title: 'Our foundation',
        paragraphs: [
          {
            text: 'Together, Wilhelm Josten and Murat Vural bring almost half a century of entrepreneurial experience from business and social contexts. This knowledge forms Viva Porta’s foundation.',
            parts: [],
          },
          {
            text: 'What truly distinguishes us is our conviction: staffing is more than placing workers. It means trust, responsibility and supporting people along their path.',
            parts: [],
          },
        ],
      },
    ],
    closing: {
      title: 'Because in the end, people always make the difference.',
      paragraphs: [
        {
          text: 'We know every professional decision carries hopes, dreams, families and plans for the future. That is why we treat every person with respect, attention and appreciation.',
          parts: [],
        },
        {
          text: 'Our goal is to bring companies and people together sustainably—not only for the next assignment, but for long-term success on both sides.',
          parts: [],
        },
        { text: 'Viva Porta stands for partnership, reliability and genuine humanity.', parts: [] },
      ],
    },
  },
};

export const commonPages: Record<LocaleWithoutGerman, Partial<Record<StaticPageKey, LocalizedPageCopy>>> = {
  tr: {
    globus: {
      globusJobs: trGlobusJobs,
      globusJobsOverview: trGlobusJobsOverview,
      eyebrow: 'Globus özel iş ortağı',
      title: 'Viva Porta ile Globus işleri.',
      intro:
        'Bu sayfada Globus’un Viva Porta ile hangi rolleri hazırladığını ve deneyiminden Almanya’da gerçekçi bir başlangıcın nasıl doğabileceğini görebilirsin.',
      heroGallery: [
        { imageAlt: 'Globus logosu' },
        { imageAlt: 'Globus mağazasının dış görünümü' },
      ],
      cards: [
        card(
          'Somut roller',
          'Fırın, kasap ve taze ürün reyonları; açık görevleri ve mağazaya görünür katkısı olan somut çalışma alanlarıdır.',
        ),
        card(
          'Kişisel destek',
          'Viva Porta, ilgini başvuruya dönüştürmeden önce deneyimini, dil seviyeni ve sonraki adımları değerlendirir.',
        ),
        card(
          'Ekibe yakın başlangıç',
          'Globus’ta ürünlerle, insanlarla ve her gün birlikte çalışan mağaza ekipleriyle yakın temas içinde çalışırsın.',
        ),
      ],
      sections: [
        section(
          'Özel iş ortağı',
          'Globus uluslararası adaylar için güçlü başlangıç noktaları oluşturuyor.',
          'Bu ortaklık somut açık rolleri kişisel hazırlıkla birleştirir. Hangi görevlerin arandığını, hangi profilin uygun olduğunu ve Viva Porta’nın seni başvuruya, dile ve Almanya’daki başlangıca nasıl hazırladığını erkenden görürsün.',
          undefined,
          'Viva Porta ve Globus ortaklık buluşmasında',
        ),
        section(
          'İş birliğinin hikâyesi',
          'Globus ile Viva Porta neden bir araya geldi?',
          'Her şey mağazalar için özellikle önemli olan taze ürün, zanaat ve danışmanlık alanlarındaki açık pozisyonlarla başladı. Viva Porta, uluslararası adayların bu alanlara pratik deneyim ve motivasyon getirdiğini gösterdi. Böylece amaç çok sayıda başvuru değil, gerçek ekiplere uygun insanlar bulmak oldu.',
          undefined,
          'Globus logosu',
        ),
        section(
          'Güncel pozisyonlar',
          'Globus tazelik, zanaat ve doğrudan müşteri teması için çalışan arıyor.',
          'Açık roller ürüne ve insana yakındır: fırın, kasap ve taze ürün reyonları. Uluslararası çalışanlar pratik deneyimlerini tam da burada görünür biçimde kullanabilir.',
          [
            card(
              'Üretim ve satışta fırın çalışanı',
              'Globus sabah üretime destek olan, ürünleri özenle hazırlayan ve satışta müşterilere güler yüzle yardımcı olan çalışanlar arıyor.',
              'Modern bir süpermarkette taze ekmekleri düzenleyen fırın çalışanı',
            ),
            card(
              'Üretim, hazırlama ve satışta kasap',
              'Kesim, üretim, tezgâh sunumu ve et ile şarküteri ürünleri hakkında danışmanlık için zanaat bilgisine sahip çalışanlar aranıyor.',
              'Aydınlık bir süpermarketin et bölümünde çalışan kasap',
            ),
            card(
              'Taze ürün reyonu çalışanı',
              'Peynir, balık ve et alanlarında ürün bilgisi, hijyen, iletişim ve müşterilere tezgahta yardımcı olma isteği önemlidir.',
              'Peynir ve balık reyonunda müşteriye yardımcı olan çalışan',
            ),
          ],
        ),
      ],
      cta: {
        eyebrow: 'Globus başvurusu',
        title: 'Globus’ta çalışmak ister misin?',
        text: 'Viva Porta’ya başvur. Fırın, kasap veya taze ürün reyonunun deneyimine uygun olup olmadığını birlikte değerlendirelim.',
        imageAlt: 'Viva Porta ekibi bir GLOBUS mağazasının kasap bölümünü ziyaret ediyor',
        primaryAction: action('Şimdi başvur'),
        secondaryAction: action('Perakendeyi incele'),
      },
    },
    'ueber-vivaporta': {
      eyebrow: 'Viva Porta hakkında',
      title: 'Kökleri, duruşu ve insana yakınlığı olan uzman kazanımı.',
      intro:
        'Viva Porta, iyi işe yerleştirmenin yalnızca işe alım olmadığını bildiği için Almanya’daki işverenlerle uluslararası uzmanları buluşturur: kültürel anlayış, temiz süreçler ve günlük işe kadar destek gerekir.',
      missionStory: missionStory.tr,
      cards: [
        card(
          'Kısa vadeli çözüm yerine ortaklık',
          'Tek tek açık pozisyonlar yerine dayanıklı personel yolları düşünüyoruz. Amaç kurumunuza, ekibinize ve uzun vadeli hedeflerinize uyan bir iş birliğidir.',
        ),
        card(
          'Adil işe alım',
          'Uluslararası işe yerleştirme uzmanlar için adil, işverenler için güvenilir olmalıdır. Bu nedenle şeffaflık, gerçekçi beklentiler ve iki taraf için kişisel destek sunuyoruz.',
        ),
        card(
          'Kalite faktörü olarak entegrasyon',
          'Bir uzmanın kalıcı olup olmayacağı çoğu zaman ülkeye geldikten sonra belli olur. Hassas başlangıç döneminde işverenleri ve yeni çalışanları destekliyoruz.',
        ),
      ],
      sections: [
        section(
          'Arka planımız',
          'İş gücü piyasası, denklik ve yaşam gerçekliği arasında çeviri yapıyoruz.',
          'Uluslararası uzman kazanımı; personel açığı, hukuki gereklilikler ve çok kişisel kararlar arasında ilerler. Alman-Türk geçmişimiz iki tarafın beklentilerini erkenden netleştirmemize yardımcı olur: İşletme neye ihtiyaç duyuyor, uzman neye ihtiyaç duyuyor ve başlangıcın yalnızca resmî değil insani olarak da işlemesi için ne hazırlanmalı?',
          [
            card(
              'Doğrudan iletişim',
              'Adaylar, işverenler ve partnerlerle gereksinimler, endişeler ve taahhütler açık kalacak şekilde iletişim kurarız.',
            ),
            card(
              'Süreç bilgisi',
              'Denklik, dil, vize ve görev planlamasını ayrı konular değil, birbirine bağlı bir başlangıç yolu olarak ele alırız.',
            ),
            card(
              'Ortak sorumluluk',
              'İşe yerleştirmeyi yalnızca bir platform hizmeti değil, uzman, işveren ve rehberlik ekibinin ortak sorumluluğu olarak görürüz.',
            ),
          ],
          'Viva Porta ekibi çalışma ortamına yaptığı ortak bir ziyarette',
        ),
        section(
          'Neden önemli?',
          'Erişim profil bulur. Destek bağlılık yaratır.',
          'Almanya uluslararası uzman göçünü kolaylaştırıyor; ancak asıl belirleyici bölüm çoğu zaman seçimden sonra başlıyor: dil pratiği, denklik, ekip kültürü, günlük hayat ve güvenilir irtibat kişileri. Viva Porta bu konuları erken görünür kılar ve yapılandırılmış biçimde destekler.',
          [
            card(
              'Adil yönelim',
              'Sorumlu uluslararası işe alım ilkelerini izler ve günlük hayatta karşılanamayacak vaatlerden kaçınırız.',
            ),
            card(
              'Hazırlıklı',
              'Dil, mesleki yeterlilik ve beklenti yönetimini erkenden birleştirerek seçim ve başlangıcı gerçekçi tutarız.',
            ),
            card(
              'Ulaşılabilir',
              'Sözleşme ve ülkeye girişten sonra da ekip, kurumlar ve günlük hayatla ilgili sorular desteğin parçası olmaya devam eder.',
            ),
          ],
        ),
        section(
          'Birlikte güçlü',
          'Viva Porta ve Globus: günlük hayatta sorumluluk taşıyan ortaklık.',
          'Globus ile uluslararası uzman kazanımını somutlaştıran bir ortaklık üzerinde çalışıyoruz. Viva Porta yapılandırılmış hazırlık, kişisel rehberlik ve kültürlerarası deneyim sunarken Globus güvenilir çalışma yerleri, net ekipler ve somut perspektifler sağlar.',
          undefined,
          'Viva Porta ve Globus ortaklık buluşmasında',
        ),
      ],
    },
    team: {
      eyebrow: 'Kurucu ekibimiz',
      title: 'İşe yerleştirmenin iki tarafını da anlayan irtibat kişileri.',
      intro:
        'Ekibimiz girişimcilik, tıbbi süreç bilgisi, Alman-Türk perspektifi ve adaylara operasyonel yakınlığı birleştirir. Bu, kısa yollar, açık iletişim ve uluslararası uzman kazanımına gerçekçi bir bakış demektir.',
      sections: [
        section(
          'Arka planımız',
          'Farkımız: yalnızca erişim değil, sistemler arasında güven kuruyoruz.',
          'Uluslararası uzman kazanımı siyasi olarak desteklenip hukuken kolaylaştırılsa da uygulamada denklik, dil, vize, işveren beklentileri, kültürel yönelim ve entegrasyon birbirine bağlıdır. Viva Porta tam bu boşlukta doğdu. Alman-Türk yakınlığını, aday süreçlerine dair deneyimi ve Alman işveren süreçlerini birleştiriyoruz.',
          [
            card(
              'Kültürel çeviri',
              'Yalnızca dili değil; ekip iletişimini, rol anlayışını, taahhütleri, soruları ve karar yollarını da çeviriyoruz.',
            ),
            card(
              'Süreç güvenliği',
              'Denklik, belgeler, dil ve ülkeye girişi tek bir yol olarak yönetir; işverenlere durum, risk ve sonraki adımlar konusunda şeffaflık sağlarız.',
            ),
            card(
              'Adil yaklaşım',
              'Uluslararası işe alım kaynak ve hedef ülkeye karşı sorumluluk gerektirir. Açık bilgi, anlaşılır beklenti ve kişisel destekle çalışırız.',
            ),
            card(
              'Pozisyon doldurmak yerine bağlılık',
              'Önemli olan yalnızca birinin başlaması değil, kalmasıdır. Bu yüzden onboarding, günlük yaşam ve entegrasyonu sözleşmeden önce düşünürüz.',
            ),
          ],
        ),
      ],
    },
    impressum: {
      eyebrow: 'Yasal bilgiler',
      title: 'Yasal bilgilendirme yükümlülükleri kapsamındaki bilgiler.',
      intro:
        'Burada Viva Porta GmbH için şirket künyesi ile zorunlu yasal bilgileri bulabilirsin.',
      cards: [
        card('Web sitesi işletmecisi', 'Enes Vural'),
        card('Şirket', 'Sınırlı sorumlu şirket (GmbH). Sorumlu kişi: Enes Vural.'),
        card('Adres', 'Mittelstraße 81, 44575 Castrop-Rauxel'),
        card('İletişim', 'E-posta: info@vivaporta.org. Telefon: +49 176 42887719.'),
        card('Şirket kaydı', 'HRB 37337, Dortmund Yerel Mahkemesi B Ticaret Sicili. KDV kimlik numarası: DE455499883.'),
        card(
          'Çevrim içi uyuşmazlık çözümü',
          'AB Komisyonu mahkeme dışı çevrim içi uyuşmazlık çözümü için bir platform sunmaktadır: https://ec.europa.eu/consumers/odr/. VSBG § 36 uyarınca bilgi.',
        ),
      ],
    },
  },
  en: {
    globus: {
      globusJobs: enGlobusJobs,
      globusJobsOverview: enGlobusJobsOverview,
      eyebrow: 'Exclusive Globus partner',
      title: 'Globus jobs with Viva Porta.',
      intro:
        'This page shows which roles Globus is currently preparing with Viva Porta and how your experience can become a realistic start in Germany.',
      heroGallery: [
        { imageAlt: 'Globus logo' },
        { imageAlt: 'Exterior of a Globus store' },
      ],
      cards: [
        card(
          'Concrete roles',
          'Bakery, butchery and fresh-food counters are tangible work areas with clear tasks and a visible contribution in store.',
        ),
        card(
          'Personal guidance',
          'Viva Porta reviews experience, language and next steps before your interest becomes an application.',
        ),
        card(
          'Start close to the team',
          'At Globus, you work with products, people and teams that need to function together in the store every day.',
        ),
      ],
      sections: [
        section(
          'Exclusive partner',
          'Globus creates strong entry points for international applicants.',
          'The partnership combines concrete vacancies with personal preparation. You see early which tasks are needed, which profile fits and how Viva Porta prepares you for the application, language and arrival.',
          undefined,
          'Viva Porta and Globus at their partnership meeting',
        ),
        section(
          'Partnership story',
          'Why Globus and Viva Porta came together.',
          'It began with conversations about vacancies in areas that matter greatly to stores: fresh food, skilled craft and customer advice. Viva Porta showed that international applicants bring practical experience and motivation to exactly these roles. The goal became clear: suitable people for real teams, not simply more applications.',
          undefined,
          'Globus logo',
        ),
        section(
          'Currently hiring',
          'Globus is looking for people for fresh food, skilled work and direct customer contact.',
          'The open roles are close to both product and people: bakery, butchery and fresh-food counters. They offer international employees a place to make their practical experience visible.',
          [
            card(
              'Bakery production and sales',
              'Globus is looking for people who help early in the day, prepare goods carefully and advise customers in a friendly way.',
              'Bakery employee arranging fresh bread in a modern supermarket',
            ),
            card(
              'Butchery production, preparation and sales',
              'Employees with practical skills are needed for cutting, production, counter presentation and advice about meat and deli products.',
              'Butcher working in a bright supermarket meat department',
            ),
            card(
              'Fresh-food counter employee',
              'Cheese, fish and meat counters require product awareness, hygiene, communication and enjoyment in helping customers directly.',
              'Employee advising a customer at a cheese and fish counter',
            ),
          ],
        ),
      ],
      cta: {
        eyebrow: 'Globus application',
        title: 'Would you like to start at Globus?',
        text: 'Contact Viva Porta. Together we will assess whether bakery, butchery or a fresh-food counter fits your experience.',
        imageAlt: 'Viva Porta visiting the butchery department of a GLOBUS store',
        primaryAction: action('Apply now'),
        secondaryAction: action('View retail'),
      },
    },
    'ueber-vivaporta': {
      eyebrow: 'About Viva Porta',
      title: 'Recruitment with roots, principles and personal proximity.',
      intro:
        'Viva Porta connects German employers with international professionals because good placement is more than recruiting: it needs cultural understanding, reliable processes and support into everyday work.',
      missionStory: missionStory.en,
      cards: [
        card(
          'Partnership, not a short-term fix',
          'We build reliable staffing paths rather than thinking in individual vacancies. The goal is collaboration that fits your organisation, team and long-term objectives.',
        ),
        card(
          'Fair recruitment',
          'International placement must be fair to professionals and reliable for employers. We focus on transparency, realistic expectations and personal guidance on both sides.',
        ),
        card(
          'Integration as a quality factor',
          'Whether a professional stays is often decided after arrival. We support employers and new employees through the sensitive starting phase.',
        ),
      ],
      sections: [
        section(
          'Our background',
          'We translate between the labour market, recognition and real life.',
          'International recruitment sits between skills shortages, legal requirements and deeply personal decisions. Our German-Turkish background helps us clarify expectations early: what does the employer need, what does the professional need, and what must be prepared for the start to work both formally and personally?',
          [
            card(
              'Direct communication',
              'We communicate with candidates, employers and partners so requirements, concerns and commitments remain transparent.',
            ),
            card(
              'Process expertise',
              'Recognition, language, visa and workforce planning are treated as one connected path, not separate topics.',
            ),
            card(
              'Shared responsibility',
              'We see placement as a shared responsibility between the professional, employer and support team—not merely a platform service.',
            ),
          ],
          'The Viva Porta team during a joint visit to the workplace',
        ),
        section(
          'Why it matters',
          'Reach finds profiles. Guidance builds retention.',
          'Germany is making skilled immigration easier, but the decisive work often begins after selection: language practice, recognition, team culture, everyday life and reliable contacts. Viva Porta makes these topics visible early and supports them in a structured way.',
          [
            card(
              'Fairly aligned',
              'We follow responsible international recruitment principles and avoid promises that cannot hold up in everyday work.',
            ),
            card(
              'Prepared',
              'Language, professional capability and expectation management are connected early so selection and arrival remain realistic.',
            ),
            card(
              'Available',
              'After contract and arrival, questions involving the team, authorities and everyday life remain part of our support.',
            ),
          ],
        ),
        section(
          'Stronger together',
          'Viva Porta and Globus: a responsible partnership in daily work.',
          'With Globus, we are building a partnership that makes international recruitment tangible. Viva Porta contributes structured preparation, personal guidance and intercultural experience; Globus provides reliable workplaces, clear teams and concrete perspectives.',
          undefined,
          'Viva Porta and Globus at their partnership meeting',
        ),
      ],
    },
    team: {
      eyebrow: 'Our founding team',
      title: 'Contacts who understand both sides of placement.',
      intro:
        'Our team combines entrepreneurship, medical process expertise, a German-Turkish perspective and operational closeness to candidates. Employers gain short communication paths, clarity and a realistic view of international recruitment.',
      sections: [
        section(
          'Our background',
          'Our difference: we build trust between systems, not only reach.',
          'International recruitment is politically supported and legally more open, yet recognition, language, visa, employer expectations, cultural orientation and integration remain closely connected in practice. Viva Porta was created for exactly this gap.',
          [
            card(
              'Cultural translation',
              'We translate more than language: team communication, role expectations, commitment, questions and decision paths.',
            ),
            card(
              'Process reliability',
              'Recognition, documents, language and entry are managed as one connected path, giving employers transparency about status, risks and next steps.',
            ),
            card(
              'Fair standards',
              'International recruitment carries responsibility towards both origin and destination countries. We work with clear information, realistic expectations and personal guidance.',
            ),
            card(
              'Retention over filling vacancies',
              'What matters is not only that someone starts, but that they stay. We therefore consider onboarding, everyday life and integration before the contract is signed.',
            ),
          ],
        ),
      ],
    },
    impressum: {
      eyebrow: 'Legal notice',
      title: 'Information required by law.',
      intro:
        'Here you can find the provider identification and mandatory legal information for Viva Porta GmbH.',
      cards: [
        card('Website operator', 'Enes Vural'),
        card('Company', 'Limited liability company (GmbH). Responsible person: Enes Vural.'),
        card('Address', 'Mittelstraße 81, 44575 Castrop-Rauxel'),
        card('Contact', 'Email: info@vivaporta.org. Phone: +49 176 42887719.'),
        card(
          'Company registration',
          'HRB 37337, Commercial Register B of Dortmund Local Court. VAT identification number: DE455499883.',
        ),
        card(
          'Online dispute resolution',
          'The European Commission provides a platform for out-of-court online dispute resolution: https://ec.europa.eu/consumers/odr/. Notice pursuant to Section 36 VSBG.',
        ),
      ],
    },
  },
};
